import CoreButton from '@components/CoreButton/CoreButton'
import ErrorIcon from '@images/icons/error.svg?react'
import {
  apiConfig,
  CardMessageTypes,
  getBackendUrl,
  MessageContext,
  postMessageToParent,
  useAuthInitQry
} from 'mmfintech-card-commons'
import type { Nullable } from 'mmfintech-card-commons/types'
import { initializeLanguage, isValidString, OtpContext } from 'mmfintech-commons'
import { Preloader } from 'mmfintech-portal-commons'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { withTranslation } from 'react-i18next'
import { Route, Switch } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import { routes } from './routes'
import theme from './theme'

import './scss/app.scss'

function AppInner() {
  const [otpOnError, setOtpOnError] = useState(null)
  const [otpOnSuccess, setOtpOnSuccess] = useState(null)

  const [parentMessage, setParentMessage] = useState<any>(null)
  const [pendingAction, setPendingAction] = useState<Nullable<string>>(null)

  const contentRef = useRef(null)

  // const dispatch = useDispatch()
  // const queryChallenge = useAppSelector(state => state.challenge)

  const { authInitialize } = useAuthInitQry()

  const resetParentMessage = useCallback(() => setParentMessage(null), [])
  const resetPendingAction = useCallback(() => setPendingAction(null), [])

  const messageContext = useMemo(
    () => ({
      parentMessage,
      resetParentMessage,
      pendingAction,
      setPendingAction,
      resetPendingAction
    }),
    [parentMessage, pendingAction, resetParentMessage, resetPendingAction]
  )

  const otpContext = useMemo(
    () => ({
      otpOnSuccess,
      setOtpOnSuccess,
      otpOnError,
      setOtpOnError
    }),
    [otpOnError, otpOnSuccess]
  )

  const allowedOrigins = useMemo(
    () =>
      [
        ...(apiConfig.DEV
          ? [apiConfig.VITE_BACKEND_URL, 'http://localhost:3000']
          : [getBackendUrl()?.replace(/cards/g, 'portal')])
      ].filter(Boolean),
    []
  )

  const messageHandler = useCallback(
    (event: MessageEvent) => {
      if (!allowedOrigins.includes(event.origin)) return

      const messageType = event.data?.type
      const messageValue = event.data?.value

      switch (messageType) {
        case CardMessageTypes.HANDSHAKE:
          if (messageValue?.token) {
            authInitialize(messageValue.token)
          }
          if (isValidString(messageValue?.languageCode)) {
            initializeLanguage(messageValue.languageCode)
          }
          break

        case CardMessageTypes.LANGUAGE:
          if (isValidString(messageValue)) {
            initializeLanguage(messageValue)
          }
          break

        case CardMessageTypes.DEPOSIT_REQUEST:
          setParentMessage(messageValue)
          break

        case CardMessageTypes.WITHDRAWAL_REQUEST:
          setParentMessage(messageValue)
          break

        case CardMessageTypes.REVEAL_DETAILS:
          setParentMessage(messageValue)
          setPendingAction(messageType)
          break
      }
    },
    [allowedOrigins, authInitialize]
  )

  useEffect(() => {
    window.addEventListener('message', messageHandler)

    let observer = null
    if (contentRef?.current) {
      const targetNode = contentRef.current

      const handleResize: ResizeObserverCallback = resize => {
        if (resize?.[0]?.target?.clientHeight) {
          postMessageToParent(CardMessageTypes.RESIZE_IFRAME, resize?.[0]?.target?.clientHeight)
        }
      }

      observer = new ResizeObserver(handleResize)
      observer.observe(targetNode)
    }

    return () => {
      observer?.disconnect()
    }
  }, [messageHandler])

  // useEffect(() => {
  //   if (isValidObject(queryChallenge) && queryChallenge?.hasOwnProperty('challengeId')) {
  //     modalShow({
  //       header: prepareTitle({ twoFactorType, userStatus }),
  //       options: { closeOnClickOutside: false },
  //       content: <Otp />
  //     })
  //   }
  // }, [queryChallenge, dispatch])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light')
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <MessageContext.Provider value={messageContext}>
        <OtpContext.Provider value={otpContext}>
          <div className='app-container' ref={contentRef}>
            <Switch>
              {routes.map(({ path, component: Component }) => (
                <Route key={path} path={path} exact render={props => <Component {...props} />} />
              ))}
              <Route path='*' component={null} />
            </Switch>
          </div>
        </OtpContext.Provider>
      </MessageContext.Provider>
    </ThemeProvider>
  )
}

function ErrorFallback({ resetErrorBoundary }) {
  return (
    <div className='app-alert-wrapper'>
      <div className='app-alert'>
        <ErrorIcon />
        <p data-test='app-alert-unexpected-error'>An unexpected error occurred</p>
        <CoreButton
          fullWidth
          data-test='app-alert-unexpected-error-back-to-home-button'
          variation='primary'
          size='normal'
          style={{ maxWidth: '30rem' }}
          onClick={resetErrorBoundary}
          text='Go back to the home page'
        />
      </div>
    </div>
  )
}

const ThisApp = withTranslation()(AppInner)

function App() {
  return (
    <Suspense
      fallback={
        <div className='app-preload-wrapper'>
          <Preloader />
        </div>
      }
    >
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(err, { componentStack }) =>
          console.error({ level: 'ERROR', componentStack, message: err.toString() })
        }
        onReset={() => {
          window.location.replace('/')
        }}
      >
        <ThisApp />
      </ErrorBoundary>
    </Suspense>
  )
}

export default App
